import { defineAsyncComponent } from 'vue';

/** @param {unknown} err */
export function isChunkLoadError(err) {
  const msg = String(err?.message ?? err ?? '');
  return (
    err?.name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

/**
 * 动态 import 失败时有限重试（部署后 hash 变更 / 弱网）
 * @template T
 * @param {() => Promise<T>} importFn
 * @param {number} [retries=2]
 * @param {number} [delayMs=400]
 * @returns {Promise<T>}
 */
export function retryImport(importFn, retries = 2, delayMs = 400) {
  return importFn().catch((err) => {
    if (!isChunkLoadError(err) || retries <= 0) throw err;
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        retryImport(importFn, retries - 1, delayMs)
          .then(resolve)
          .catch(reject);
      }, delayMs);
    });
  });
}

function notifyChunkLoadFailed() {
  // RouteChunkFallback / 路由 fallback 页已展示内嵌错误，不再弹全局 Toast。
}

/**
 * 路由级 lazy view：chunk 失败时 toast，不抛错导致白屏
 * @param {() => Promise<{ default: import('vue').Component }>} importFn
 */
export function loadRouteView(importFn) {
  return retryImport(importFn, 1).catch((err) => {
    if (isChunkLoadError(err)) {
      notifyChunkLoadFailed();
      return import('@/views/fallback/RouteChunkFallback.vue');
    }
    throw err;
  });
}

/**
 * defineAsyncComponent 封装：chunk 失败可重试，最终失败仅 toast 不拖垮父页面
 * @param {() => Promise<{ default: import('vue').Component }>} importFn
 * @param {{ maxAttempts?: number }} [options]
 */
export function createLazyComponent(importFn, options = {}) {
  const maxAttempts = options.maxAttempts ?? 3;

  return defineAsyncComponent({
    loader: () => retryImport(importFn, 1),
    onError(err, retry, fail, attempts) {
      if (isChunkLoadError(err) && attempts < maxAttempts) {
        retry();
        return;
      }
      fail();
      if (isChunkLoadError(err)) {
        notifyChunkLoadFailed();
      }
    },
  });
}

/**
 * 注册全局 chunk 错误兜底（路由 + 未捕获 Promise）
 * @param {import('vue-router').Router} router
 */
export function setupChunkLoadRecovery(router) {
  router.onError((err) => {
    if (!isChunkLoadError(err)) return;
    notifyChunkLoadFailed();
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (!isChunkLoadError(event.reason)) return;
    event.preventDefault();
    notifyChunkLoadFailed();
  });
}
